/* global fbq */
/**
 *
 *  Lifestyle Series of Classes page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 26/08/2025.
 *  Copyright (c) 2025-present iWoo Ltd. All rights reserved.
 *
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Title, Meta, Link as LinkMeta } from "react-meta-elements";

import { useAppState } from "../../state";
import { useRegion } from "../../context/RegionProvider";

import {
  useGetEvents,
  useGetSeries,
  useGetSeriesPublic,
} from "../../hooks/useEvents/useEvents";

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../Button/Button";
import EventSeriesCard from "./EventSeriesCard/EventSeriesCard";
import BreadcrumbNav from "../BreadcrumbNav/BreadcrumbNav";
import { CityPickerLS } from "../CityPickerModal/CityPickerModal";

// Hooks
import useLatest from "../../hooks/useLatest/useLatest";

// Icons
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";
import MapMarkerIcon from "../../icons/MapMarkerIcon";
import CityPickerButton from "../CityPickerButton/CityPickerButton";
import EventCardPlaceholder from "../EventCardPlaceholder/EventCardPlaceholder";

// Images

export function SeriesList({}) {
  const { region, city } = useRegion();
  const { user, clearElevatedEventsLoaded } = useAppState();

  const [isCityPickerModalOpen, setIsCityPickerModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);

  const selectedCityLatest = useLatest(selectedCity);

  const heroRef = React.useRef(null);
  const sliderRef = React.useRef(null);

  const { data: seriesData, isLoading: isSeriesLoading } = useGetSeries({
    user,
    region,
    city,
  });

  const { data: seriesDataPublic, isLoading: isSeriesLoadingPublic } =
    useGetSeriesPublic({ user, region, city });

  const series = useMemo(() => {
    return user ? seriesData : seriesDataPublic;
  }, [user, seriesData, seriesDataPublic]);

  const isUserLoggedIn = !!(user && user.id);

  const handleCityClick = useCallback(() => {
    setIsCityPickerModalOpen(true);
  }, []);

  const handleCityChange = useCallback(
    (city) => {
      if (selectedCityLatest.current === city) {
        return;
      }

      setSelectedCity(city);
    },
    [user]
  );

  return (
    <>
      <ScrollToTopOnMount />
      <Title>TruBe: Unique Locations, Unforgettable Fitness Events</Title>
      <Meta
        name="description"
        content="TruBe: Unique Locations, Unforgettable Fitness classes in Pilates, Yoga, HIIT, Cardio, and more. Providing the best coaches and fitness and wellbeing experience."
      />
      <LinkMeta rel="canonical" href="https://trubeapp.com/events/upcoming" />
      <Header
        showRegionSelect={false}
        isFixed
        isLoggedIn={isUserLoggedIn}
        returnTo="/lifestyle"
        heroRef={heroRef}
        onSignUpClick={clearElevatedEventsLoaded}
        onLoginClick={clearElevatedEventsLoaded}
      />

      <div className="tbk-bg-grey-light_ tbk-overflow-hidden">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div
            ref={sliderRef}
            className="tb-slider tbk-mb-3 tbk-mt-3 tbk-flex tbk-flex-nowrap tbk-items-center"
          >
            <BreadcrumbNav to={"/lifestyle"}>
              Lifestyle collective
            </BreadcrumbNav>

            <ArrowRightIcon className="tbk-text-blue-grey-light" />

            <BreadcrumbNav>Series of Classes</BreadcrumbNav>
          </div>
        </div>
      </div>

      <div className="tbk-bg-grey-light_ tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-basic-white tbk-pb-4 tbk-pt-4 lg:tbk-pt-4">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <div className="tbk-mb-4 tbk-flex tbk-flex-col tbk-items-center tbk-justify-center">
            <h2 className="tbk-text-title-bold lg:tbk-text-title-bold 2 7 7 tbk-mb-4 tbk-px-2 tbk-text-center tbk-text-blue-grey lg:tbk-mb-4">
              Series of Classes
            </h2>

            {region === "US" && (
              <CityPickerButton
                className="tbk-mt-2_ tbk-mb-4_"
                city={selectedCity}
                onCityChange={handleCityChange}
              />
            )}
          </div>
          {region === "US" && false ? (
            <button
              className={`tbk-mx-auto tbk-mt-4 tbk-flex tbk-max-w-full tbk-items-center tbk-rounded-lg tbk-border tbk-border-solid tbk-border-grey-inactive ${
                selectedCity ? "tbk-py-0.5" : "tbk-py-1"
              } tbk-px-1 tbk-shadow-trube lg:tbk-max-w-xs`}
              onClick={handleCityClick}
            >
              <MapMarkerIcon className="tbk-shrink-0" />
              <div className="tbk-text-small tbk-mr-1 tbk-whitespace-nowrap tbk-text-blue-dark">
                Location:
              </div>
              <div
                className={`location-link tbk-text-small tbk-block tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap ${
                  selectedCity ? "tbk-text-grey" : "tbk-text-grey"
                }`}
              >
                {adaptCityName(selectedCity)}
              </div>
            </button>
          ) : null}

          <div className="tbk-mb-4 tbk-grid tbk-gap-2 md:tbk-grid-cols-2 lg:tbk-grid-cols-3 ">
            {series &&
            series.data &&
            series.data.content &&
            series.data.content.length &&
            !isSeriesLoading &&
            !isSeriesLoadingPublic
              ? series.data.content.map((evt, index) => (
                  <EventSeriesCard
                    key={index}
                    href={`/events/series/${evt.id}`}
                    imgUrl={evt.coverPhoto}
                    title={evt.name}
                    startDate={evt.start}
                    endDate={evt.finish}
                    classesCount={evt.eventCount}
                    joined={evt.joined}
                    status={evt.status}
                    region={region}
                    // onClick={handleEventCardClick}
                  />
                ))
              : [...new Array(6)].map((v) => <EventCardPlaceholder key={v} />)}
          </div>
        </div>
      </div>

      {/*{hasMoreGatedEvents && !isElevatedEventsFetching ? (*/}
      {/*  <Button*/}
      {/*    variant="secondary"*/}
      {/*    className="tbk-mt-7_ tbk-mx-auto tbk-mb-2 tbk-mb-7 tbk-w-68 md:tbk-mb-7"*/}
      {/*  >*/}
      {/*    Show more*/}
      {/*  </Button>*/}
      {/*) : null}*/}

      <div className="tbk-mb-7 tbk-flex tbk-justify-center">
        <Link to={"/lifestyle"}>
          <Button
            className="tbk-w-40 tbk-text-primary"
            variant="secondary"
            size={"md"}
            startIcon={<ArrowLeftIcon className="tbk-tex-primary" />}
          >
            Back
          </Button>
        </Link>
      </div>

      <Footer regionSelect={false} />

      <CityPickerLS
        isOpen={isCityPickerModalOpen}
        city={selectedCity}
        onClose={() => setIsCityPickerModalOpen(false)}
        onCityChange={handleCityChange}
      />
    </>
  );
}

const adaptCityName = (cityName) => {
  return cityName
    ? cityName === "LOS_ANGELES"
      ? "Los Angeles"
      : cityName === "SAN_DIEGO"
      ? "San Diego"
      : "All"
    : "All";
};
